import {Component, OnDestroy, OnInit} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {AuthenticationService} from '../../../authenticationModule/services/authentication.service';
import {UtilsService} from '../../../appSharedModule/services/utils.service';
import {UserLogged} from '../../../models/userLogged';
import {MenuSection} from '../../../Enum/menu-section.enum';
import {Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';

@Component({
  selector: 'app-side-bar',
  templateUrl: './side-bar.component.html',
  styleUrls: ['./side-bar.component.css']
})
export class SideBarComponent implements OnInit, OnDestroy {
  public currentUser: UserLogged;
  public menuSections = MenuSection;
  public collapsedSections: Record<string, boolean> = {};
  private readonly collapseSectionsByDefault = false;
  private readonly collapsedStateStorageKey = 'sidebarCollapsedSections';
  private readonly sectionKeys: string[] = [
    'documents',
    'clients',
    'portal_admin',
    'users',
    'crossConnects',
    'bandwidth',
    'marketData',
    'PTE'
  ];

  // IMPORTANT: keep the sectionRoutePatterns map in sync with side-bar.component.html links so sections auto-expand correctly when new routes are added or actual routes are modified for some reason
  private readonly sectionRoutePatterns: Record<string, RegExp[]> = {
    documents: [
      /^\/commercial-pop/,
      /^\/document(\/|$)/,
      /\/announcementLog/
    ],
    clients: [
      /\/company(\/|$)/,
      /\/impersonate-user/
    ],
    portal_admin: [
      /\/historical-data-log/,
      /\/download-log/,
      /\/symbolDataDownloadLog/
    ],
    users: [
      /\/edit(\/|$)/,
      /\/users(\/|$)/,
      /\/manage-inv-recipient/
    ],
    crossConnects: [
      /^\/loa/,
      /^\/hosting/,
      /^\/service-delivery-locations/,
      /^\/market-data-ledgers/
    ],
    bandwidth: [
      /^\/schedules/,
      /^\/schedule\/etree/,
      /^\/bandwidth-graphs/,
      /\/history-request/,
      /\/etrees-counters-live/,
      /\/counters-mqtt/,
      /\/bandwidth-monitoring/,
      /\/quincy-etrees/,
      /\/crypto-monitoring/
    ],
    marketData: [
      /\/dad-admin/,
      /\/encryption-keys/,
      /\/encryption-keys-logs/,
      /\/market-data-/,
      /\/qed-symbols/,
      /\/qrd-symbols/,
      /\/qrd-products/,
      /\/quincyClientProfile\//,
      /\/qfd-symbols/,
      /\/quincy-news-symbols/,
      /\/exchanges/,
      /\/reporters/
    ],
    PTE: [
      /\/timeSyncProfile/,
      /\/time-sync-mqtt/,
      /\/time-sync\b/
    ]
  };
  private routerSubscription?: Subscription;
  public schedulesRouteActive = false;

  constructor(public authenticationService: AuthenticationService,
              public utilsService: UtilsService,
              private router: Router) {
  }

  ngOnInit() {
    this.restoreCollapsedState();
    this.sectionKeys.forEach(key => {
      if (this.collapsedSections[key] === undefined) {
        this.collapsedSections[key] = this.collapseSectionsByDefault;
      }
    });
    this.currentUser = this.authenticationService.getUserCurrent();
    this.handleRouteChange(this.router.url);
    this.routerSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(event => this.handleRouteChange(event.urlAfterRedirects));
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
  }

  public toggleSection(sectionKey: string): void {
    this.collapsedSections[sectionKey] = !this.collapsedSections[sectionKey];
    this.persistCollapsedState();
  }

  public expandAll(): void {
    this.setAllSections(false);
  }

  public collapseAll(): void {
    this.setAllSections(true);
  }

  public isCollapsed(sectionKey: string): boolean {
    return this.collapsedSections[sectionKey] ?? this.collapseSectionsByDefault;
  }

  public onSectionHeaderKeydown(event: KeyboardEvent, sectionKey: string): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleSection(sectionKey);
    }
  }

  private setAllSections(collapsed: boolean): void {
    this.sectionKeys.forEach(key => (this.collapsedSections[key] = collapsed));
    this.persistCollapsedState();
  }

  private persistCollapsedState(): void {
    try {
      localStorage.setItem(this.collapsedStateStorageKey, JSON.stringify(this.collapsedSections));
    } catch (e) {
      // Ignore storage errors to avoid breaking the sidebar
    }
  }

  private restoreCollapsedState(): void {
    try {
      const stored = localStorage.getItem(this.collapsedStateStorageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && typeof parsed === 'object') {
          this.collapsedSections = {...parsed};
        }
      }
    } catch (e) {
      this.collapsedSections = {};
    }
  }

  private handleRouteChange(url: string): void {
    const normalizedUrl = this.normalizeUrl(url);
    const sectionKey = this.findSectionKeyForUrl(normalizedUrl);
    if (sectionKey && this.isCollapsed(sectionKey)) {
      this.collapsedSections[sectionKey] = false;
      this.persistCollapsedState();
    }
    this.schedulesRouteActive = this.isSchedulesPath(normalizedUrl);
  }

  private findSectionKeyForUrl(url: string): string | undefined {
    if (!url) {
      return undefined;
    }
    return Object.entries(this.sectionRoutePatterns).find(([, patterns]) =>
      patterns.some(pattern => pattern.test(url))
    )?.[0];
  }

  private normalizeUrl(url: string): string {
    if (!url) {
      return '';
    }
    const [path] = url.split('?');
    return path;
  }

  public isSchedulesLinkActive(): boolean {
    return this.schedulesRouteActive;
  }

  private isSchedulesPath(url: string): boolean {
    return /^\/schedules(\/|$)/.test(url) || /^\/schedule\/etree(\/|$)/.test(url);
  }
}
